/**
 * Pure presentation helpers for the contact-based plan ladder (V46). Every
 * number comes from the server catalog; these only format and select.
 */
import { t, type MessageKey } from '@/i18n';
import { isClaimLive } from '@/config/featureAvailability';
import type { CatalogTier, ContactUsage } from '@/services/billingPlans';

export function formatContacts(n: number): string {
  return n.toLocaleString('en-US');
}

/** The feature lines a tier card shows, in the order the pricing page uses. */
export function tierFeatures(tier: CatalogTier): string[] {
  const lines: string[] = [];
  lines.push(
    tier.contactCap === null
      ? t('billing.plans.feature.customContacts')
      : t('billing.plans.feature.contacts', { count: formatContacts(tier.contactCap) }),
  );
  lines.push(tier.channels === 'all' ? t('billing.plans.feature.allChannels') : t('billing.plans.feature.oneChannel'));
  lines.push(
    tier.maxActiveFlows === null
      ? t('billing.plans.feature.unlimitedFlows')
      : t('billing.plans.feature.flows', { count: tier.maxActiveFlows }),
  );
  lines.push(
    tier.inboxSeatsIncluded === 1
      ? t('billing.plans.feature.inboxSeat')
      : t('billing.plans.feature.inboxSeats', { count: tier.inboxSeatsIncluded }),
  );
  return lines;
}

/**
 * Whether a feature line names something a customer who signs up today cannot
 * use yet (the messaging channels wait on Meta App Review and WhatsApp
 * onboarding — config/featureAvailability.ts). The pricing card shows it with
 * the Coming-soon badge.
 */
export function isComingSoonFeatureLine(line: string): boolean {
  const keys: MessageKey[] = ['billing.plans.feature.allChannels', 'billing.plans.feature.oneChannel'];
  return keys.some((key) => line === t(key) && !isClaimLive(key));
}

export function priceLabel(tier: CatalogTier): string {
  return tier.priceUsd === null ? t('billing.plans.customPrice') : `$${tier.priceUsd}`;
}

/** Width (0–100) of the contact usage bar. */
export function usageBarPct(usage: Pick<ContactUsage, 'count' | 'cap'>): number {
  if (usage.cap <= 0) return 0;
  return Math.min(100, Math.round((usage.count / usage.cap) * 100));
}
